import { Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { LogonService } from '@sabstravtech/obtservices/angular';
import { MfaReissueResponse } from '@sabstravtech/obtservices/base';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { ThemeService } from '../../core/services/theme.service';
import { LightningModalTypes } from '../../core/classes/modal-types.enum';

const MFA_RESEND_COOLDOWN_SECONDS = 60;
const MFA_CODE_LENGTH = 6;

@Component({
  selector: 'app-mfa-code',
  templateUrl: './mfa-code.component.html',
  styleUrls: ['./mfa-code.component.scss'],
  standalone: false,
  // ViewEncapsulation.None required because NgbModal renders content at body level,
  // outside Angular's component view hierarchy, so scoped styles don't apply
  encapsulation: ViewEncapsulation.None
})
export class MfaCodeComponent implements OnInit, OnDestroy {
  public logoUrl =
    'https://images.sabscorp.com/images/enterprise/lightUK/assets/images/lightning_logo.svg';
  public readonly codeLength = MFA_CODE_LENGTH;

  public mfaCode = '';
  public userId = 0;
  public userEmail = '';
  public loading = false;
  public resending = false;
  public error = '';
  public resendMessage = '';
  public resendDisabled = false;
  public resendCountdown = 0;
  private countdownInterval: number = null;
  private subscriptions: Subscription[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private logonService: LogonService,
    public themeService: ThemeService,
    protected translateService: TranslateService
  ) {}

  get maskedEmail(): string {
    if (!this.userEmail) {
      return '';
    }
    const [localPart, domain] = this.userEmail.split('@');
    if (!domain) {
      return this.userEmail;
    }
    const maskedLocal =
      localPart.length > 2
        ? localPart.substring(0, 2) + '*'.repeat(Math.min(localPart.length - 2, 8))
        : localPart;
    const domainParts = domain.split('.');
    const maskedDomain =
      domainParts[0].length > 2
        ? domainParts[0].substring(0, 2) + '*'.repeat(Math.min(domainParts[0].length - 2, 6))
        : domainParts[0];
    return `${maskedLocal}@${maskedDomain}.${domainParts.slice(1).join('.')}`;
  }

  ngOnInit(): void {
    // MFA code is sent by the backend during login, so we just start the resend cooldown
    this.startResendCountdown(MFA_RESEND_COOLDOWN_SECONDS);
    this.logoUrl = this.themeService.getLogoMyBookings();
  }

  ngOnDestroy(): void {
    this.clearCountdown();
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private clearCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private startResendCountdown(seconds: number): void {
    this.resendDisabled = true;
    this.resendCountdown = seconds;
    this.clearCountdown();
    this.countdownInterval = window.setInterval(() => {
      this.resendCountdown--;
      if (this.resendCountdown <= 0) {
        this.clearCountdown();
        this.resendDisabled = false;
      }
    }, 1000);
  }

  submitCode(): void {
    this.error = '';
    this.resendMessage = '';

    if (!this.mfaCode || this.mfaCode.length !== MFA_CODE_LENGTH) {
      this.error = `Please enter the ${MFA_CODE_LENGTH}-digit code from your email`;
      return;
    }

    this.loading = true;
    const sub = this.logonService.submitMfaCode(this.userId, this.mfaCode).subscribe({
      next: (result) => {
        this.loading = false;
        this.activeModal.close({ success: true, result });
      },
      error: (err) => {
        this.loading = false;
        this.error = this.getErrorMessage(err?.error?.reason);
        this.mfaCode = '';
      }
    });
    this.subscriptions.push(sub);
  }

  resendCode(): void {
    if (this.resendDisabled || this.resending) {
      return;
    }

    this.error = '';
    this.resendMessage = '';
    this.resending = true;

    const sub = this.logonService.resendMfaCode().subscribe({
      next: (response: MfaReissueResponse) => {
        this.resending = false;
        if (response.existingTokenTtl) {
          const seconds = Math.ceil(response.existingTokenTtl / 1000);
          this.resendMessage = `A code was recently sent. Please wait ${seconds} seconds before requesting another.`;
          this.startResendCountdown(seconds);
        } else {
          this.resendMessage = response.message || 'A new code has been sent to your email';
          this.startResendCountdown(MFA_RESEND_COOLDOWN_SECONDS);
        }
      },
      error: (err) => {
        this.resending = false;
        this.error = err?.error?.message || 'Failed to resend code. Please try again.';
      }
    });
    this.subscriptions.push(sub);
  }

  close(): void {
    this.activeModal.dismiss('cancelled');
  }

  private getErrorMessage(reason: string): string {
    const errorMessages: Record<string, string> = {
      MFA_ATTEMPTS_EXCEEDED:
        'Too many incorrect attempts. Please wait a few minutes and try logging in again.',
      INVALID_JWT: 'Your session has expired. Please close this window and log in again.',
      OP_NOT_SUPPORTED:
        'Multi-factor authentication is not available for your account. Please contact support.',
      MFA_WRONG_TOKEN: 'The code you entered is incorrect. Please check your email and try again.',
      MFA_TOKEN_EXPIRED:
        'Your token has expired. Please click on the Resend button to receive a new token.'
    };
    const message = errorMessages[reason];
    if (message) {
      return this.translateService.instant(message);
    }
    return reason || this.translateService.instant('Invalid code. Please try again.');
  }

  onCodeInput(event: Event): void {
    // Only allow digits, max 6 characters
    const input = event.target as HTMLInputElement;
    const filtered = (input.value || '').replace(/[^0-9]/g, '').substring(0, MFA_CODE_LENGTH);
    if (input.value !== filtered) {
      input.value = filtered;
    }
    this.mfaCode = filtered;
  }
}

LightningModalTypes.MfaCodeComponent.component = MfaCodeComponent;
